import { Calendar, Clock, MessageSquare, Users } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import reservationApi from "../../services/reservationApi";
import type { ReserveTable } from "../../types/reservations";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";

interface UpcomingReservationsCardProps {
  onTableClick: (tableId: string) => void;
  selectedFloor: "floor1" | "floor2" | "rooftop";
  selectedTableId?: string | null;
}

interface ReservationWithTable extends ReserveTable {
  tableName?: string;
}

export default function UpcomingReservationsCard({
  onTableClick,
  selectedFloor,
  selectedTableId,
}: Readonly<UpcomingReservationsCardProps>) {
  const [upcomingReservations, setUpcomingReservations] = useState<
    ReservationWithTable[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  
  // Filter state: 'today' | 'all' | 'date'
  const [filterType, setFilterType] = useState<'today' | 'all' | 'date'>('today');
  const [selectedDate, setSelectedDate] = useState<string>(''); // YYYY-MM-DD format for custom date filter
  // Helper to get floor ID from floor key
  const getFloorId = (floor: string): number => {
    switch (floor) {
      case 'floor1': return 1;
      case 'floor2': return 2;
      case 'rooftop': return 3;
      default: return 1;
    }
  };

  const loadUpcomingReservations = useCallback(async () => {
    try {
      setIsLoading(true);

      // Get current date
      const now = new Date();
      const currentDate = now.toISOString().split("T")[0];
      const currentFloorId = getFloorId(selectedFloor);

    
      let backendType: string = filterType;
      let backendDate: string | undefined = undefined;
      
      if (filterType === 'date') {
        if (selectedDate && selectedDate.trim() !== '') {
          backendType = 'date';
          backendDate = selectedDate;
        } else {
          backendType = 'today';
        }
      }
      
      //console.log(`📊 Loading reservations for floor ${selectedFloor} (floorId: ${currentFloorId}), filterType: ${filterType}, backendType: ${backendType}, date: ${backendDate || 'none'}`);

      // Use the optimized floor-based endpoint
      let allReservations: ReserveTable[] = [];

      try {
        // Use admin endpoint with floorId, type, and date for optimized backend filtering
        const acceptedBookingsPage = await reservationApi.getAcceptedBookings(
          0,
          100,
          currentFloorId,  // Pass floor ID to filter on backend
          backendType,     // Pass type (today/all) to filter on backend
          backendDate      // Pass date for date-specific filtering (only for 'date' filter)
        );
        allReservations = acceptedBookingsPage.content || [];
        //console.log(`✅ Loaded ${allReservations.length} accepted bookings from optimized endpoint (floor: ${currentFloorId}, type: ${backendType}, date: ${backendDate || 'none'})`);

      } catch (adminError) {
        toast.error(`⚠️ Admin endpoint failed, falling back to period search: ${adminError}`);
        // Fallback to period search if admin endpoint fails
        const todayReservations =
          await reservationApi.searchReservationsByPeriodWithParams(
            currentDate,
            "today"
          );
        const weekReservations =
          await reservationApi.searchReservationsByPeriodWithParams(
            currentDate,
            "week"
          );

        // Combine and deduplicate reservations
        const allReservationsMap = new Map<string, ReserveTable>();
        [...todayReservations, ...weekReservations].forEach((reservation) => {
          const key = reservation.id?.toString() || reservation.bookedId || "";
          if (key && !allReservationsMap.has(key)) {
            // Cast ReserveTableDTO to ReserveTable since they're compatible
            allReservationsMap.set(key, reservation as ReserveTable);
          }
        });
        allReservations = Array.from(allReservationsMap.values());
      }

      // console.log("📊 All reservations fetched:", {
      //   total: allReservations.length,
      //   reservations: allReservations.map((r) => ({
      //     id: r.id,
      //     bookedId: r.bookedId,
      //     status: r.status,
      //     date: r.date,
      //     time: r.time,
      //     tables: r.restaurantTableId?.map((t) => ({ id: t.id, name: t.name, floorId: (t as { id: number; name: string; floorId?: number }).floorId })),
      //     tableCount: r.restaurantTableId?.length || 0,
      //   })),
      // });

      // Log any reservations with multiple tables
      const multiTableReservations = allReservations.filter(
        (r) => r.restaurantTableId && r.restaurantTableId.length > 1
      );
      // if (multiTableReservations.length > 0) {
      //   console.log(
      //     "🔗 Found reservations with multiple tables:",
      //     multiTableReservations.map((r) => ({
      //       bookedId: r.bookedId,
      //       tables: r.restaurantTableId?.map((t) => ({
      //         id: t.id,
      //         name: t.name,
      //       })),
      //     }))
      //   );
      // }

      //console.log("🔍 Selected table ID for filtering:", selectedTableId);

      // Filter reservations - since backend already filters by floor, type (today/all), and date,
      // we only need to:
      // 1. Filter for accepted status (safety check)
      // 2. Filter by selected table if one is selected
      const filteredReservations = allReservations
        .filter((reservation) => {
          // Only show accepted reservations (safety check - backend should only return accepted)
          if (reservation.status !== "Accepted") return false;

          // If a specific table is selected, only show reservations for that table
          if (selectedTableId) {
            const hasMatchingTable = reservation.restaurantTableId?.some(
              (table) => table.id?.toString() === selectedTableId
            );
            if (!hasMatchingTable) return false;
          }

          return true;
        })

        .map((reservation) => ({

          ...reservation,
          tableName:
            reservation.restaurantTableId?.map((t) => t.name).join(", ") ||
            "Unknown Table",
        }))
        .sort((a, b) => {
          // Sort by date and time (earliest first)
          const dateTimeA = new Date(`${a.date}T${a.time}`);
          const dateTimeB = new Date(`${b.date}T${b.time}`);
          return dateTimeA.getTime() - dateTimeB.getTime();
        });

      // console.log("📋 Final filtered reservations:", {
      //   selectedTableId,
      //   selectedFloor,
      //   currentFloorId,
      //   totalCount: filteredReservations.length,
      //   showingToday: !selectedTableId,
      //   reservations: filteredReservations.map((r) => ({
      //     id: r.id,
      //     bookedId: r.bookedId,
      //     customer: r.username,
      //     date: r.date,
      //     time: r.time,
      //     tables: r.tableName,
      //   })),
      // });

      setUpcomingReservations(filteredReservations);
    } catch (error) {
      toast.error("Failed to load upcoming reservations");
    } finally {
      setIsLoading(false);
    }
  }, [selectedTableId, selectedFloor, filterType, selectedDate]);


  // Load reservations on mount and when filter changes
  useEffect(() => {
    loadUpcomingReservations();
  }, [loadUpcomingReservations, selectedFloor, filterType, selectedDate]);

  // Get today's date in YYYY-MM-DD format for date picker min value
  const getTodayDate = () => new Date().toISOString().split('T')[0];


  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case "Accepted":
        return "bg-green-100 text-green-800";
      case "Pending":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatTime = (time: string) => {
    try {
      const [hours, minutes] = time.split(":");
      const date = new Date();
      date.setHours(Number.parseInt(hours), Number.parseInt(minutes));
      return date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return time;
    }
  };

  const handleTableClick = (reservation: ReservationWithTable) => {
    // Find the first table ID from the reservation
    const firstTableId = reservation.restaurantTableId?.[0]?.id;
    if (firstTableId) {
      onTableClick(firstTableId.toString());
    }
  };

  return (
    <div className="pt-4 border-t border-gray-200">
      {/* Filter Buttons */}
      <div className="mb-4 space-y-2">
        <div className="flex gap-2">
          <Button
            variant={filterType === 'today' ? 'blueBg' : 'outline'}
            size="sm"
            onClick={() => {
              setFilterType('today');
              setSelectedDate('');
            }}
            className="flex-1 text-xs"
          >
            Today
          </Button>
          <Button
            variant={filterType === 'all' ? 'blueBg' : 'outline'}
            size="sm"
            onClick={() => {
              setFilterType('all');
              setSelectedDate('');
            }}
            className="flex-1 text-xs"
          >
            All
          </Button>
          <Button
            variant={filterType === 'date' ? 'blueBg' : 'outline'}
            size="sm"
            onClick={() => setFilterType('date')}
            className="flex-1 text-xs"
          >
            <Calendar className="w-3 h-3 mr-1" />
            Date
          </Button>
        </div>
        
        {/* Date Picker - shows when Date filter is selected */}
        {filterType === 'date' && (
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={selectedDate || getTodayDate()}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="text-center py-4">
          <div className="text-sm text-gray-500">
            Loading reservations...
          </div>
        </div>

      ) : upcomingReservations.length === 0 ? (
        <div className="text-center py-4">
          
          <div className="text-sm text-gray-500">
            {selectedTableId
              ? "No reservations for this table"
              : filterType === 'today'
                ? "No reservations for today"
                : filterType === 'all'
                  ? "No reservations found"
                  : `No reservations for ${selectedDate || 'selected date'}`}
          </div>
        </div>

      ) : (
        <div className="space-y-2 max-h-full overflow-y-auto">
          {upcomingReservations.map((reservation) => (
            <button
              key={reservation.id || reservation.bookedId}
              type="button"
              className="w-full p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200 text-left"
              onClick={() => handleTableClick(reservation)}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-sm text-gray-900">
                      {reservation.username}
                    </span>
                    <Badge
                      className={`text-xs ${getStatusBadgeColor(
                        reservation.status
                      )}`}
                    >
                      {reservation.status}
                    </Badge>
                  </div>

                  <div className="grid grid-cols-2 gap-1 text-xs text-gray-600">
                    <div className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {new Date(reservation.date).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                      })}
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {formatTime(reservation.time)}
                    </div>
                    <div className="flex items-center gap-1">
                      <Users className="h-3 w-3" />
                      {reservation.guestNo} guests
                    </div>
                    <div className="flex items-center gap-1">
                      <MessageSquare className="h-3 w-3" />
                      <span className="truncate">{reservation.tableName}</span>
                    </div>
                  </div>
                </div>
              </div>

              {reservation.message && (
                <div className="text-xs text-gray-500 mt-2 p-2 bg-white rounded border">
                  <span className="font-medium">Note:</span>{" "}
                  {reservation.message}
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      {/* <div className="mt-3 pt-3 border-t border-gray-200">
        <div className="text-xs text-gray-500 text-center">
          Click on a reservation to highlight the table on the floor plan
        </div>
      </div> */}
    </div>
  );
}
